import { useLayoutEffect, useRef, useState, useEffect } from "react";
import { gsap, ScrollTrigger } from "@/lib/gsap";

// w = desktop width, mw = mobile width, h = height, y = desktop vertical offset
const LAYOUT = [
  { kind: "img", w: "26vw", h: "76vh", mw: "72vw" },
  { kind: "text", text: "REAL PEOPLE." },
  { kind: "img", w: "18vw", h: "46vh", mw: "50vw", y: "-12vh" },
  { kind: "img", w: "44vw", h: "62vh", mw: "82vw" },
  { kind: "img", w: "22vw", h: "68vh", mw: "58vw", y: "8vh" },
  { kind: "text", text: "REAL MOMENTS." },
  { kind: "img", w: "34vw", h: "52vh", mw: "78vw", y: "-8vh" },
  { kind: "img", w: "18vw", h: "48vh", mw: "50vw", y: "12vh" },
  { kind: "img", w: "40vw", h: "78vh", mw: "84vw" },
  { kind: "text", text: "REAL ENERGY." },
  { kind: "img", w: "24vw", h: "70vh", mw: "62vw" },
  { kind: "img", w: "36vw", h: "56vh", mw: "80vw", y: "-6vh" },
];

export default function HorizontalGallery({ images }) {
  const root = useRef(null);
  const track = useRef(null);
  const [mobile, setMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setMobile(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  useLayoutEffect(() => {
    if (!images.length) return undefined;
    const ctx = gsap.context(() => {
      const dist = () => Math.max(0, track.current.scrollWidth - window.innerWidth);
      gsap.to(track.current, {
        x: () => -dist(),
        ease: "none",
        scrollTrigger: { trigger: root.current, start: "top top", end: () => `+=${dist()}`, pin: true, scrub: 0.8, invalidateOnRefresh: true, anticipatePin: 1, pinType: "fixed", refreshPriority: 2 },
      });
      gsap.utils.toArray(root.current.querySelectorAll(".hg-img img")).forEach((img) => {
        gsap.fromTo(img, { xPercent: -6 }, { xPercent: 6, ease: "none", scrollTrigger: { trigger: root.current, start: "top top", end: () => `+=${dist()}`, scrub: true } });
      });
      ScrollTrigger.refresh();
    }, root);
    return () => ctx.revert();
  }, [images, mobile]);

  let idx = 0;
  return (
    <section ref={root} id="portfolio" className="relative h-[100svh] w-full overflow-hidden bg-[#080A0C]" data-testid="horizontal-gallery">
      <div ref={track} className="flex h-full items-center gap-[8vw] sm:gap-[6vw] pl-[6vw] sm:pl-[8vw] pr-[8vw] sm:pr-[10vw] w-max will-change-transform">
        {LAYOUT.map((item, i) => {
          if (item.kind === "text") {
            return (
              <h2 key={i} className="font-display font-extrabold text-white text-[clamp(2rem,7vw,7rem)] leading-[0.9] tracking-[-0.03em] whitespace-nowrap shrink-0" data-testid={`gallery-text-${i}`}>
                {item.text}
              </h2>
            );
          }
          const src = images[idx % images.length];
          idx += 1;
          return (
            <figure
              key={i}
              className="hg-img relative shrink-0 overflow-hidden"
              style={{ width: mobile ? item.mw : item.w, height: item.h, transform: item.y && !mobile ? `translateY(${item.y})` : undefined }}
              data-cursor="view"
              data-testid={`gallery-img-${i}`}
            >
              <img src={src} alt="LUHAI photography" className="h-full w-[112%] max-w-none object-cover object-[50%_32%]" loading="lazy" />
            </figure>
          );
        })}
      </div>
      <div className="absolute bottom-6 left-5 sm:left-8 font-mono2 text-[10px] tracking-[0.3em] text-white/40">SELECTED WORK — SCROLL</div>
    </section>
  );
}
